<template>
  <div class="embedded-stripe-checkout">
    <!-- Loading State -->
    <div v-show="loading" class="text-center py-8">
      <Icon name="lucide:loader-2" class="w-6 h-6 animate-spin mx-auto mb-2" />
      <p class="text-sm text-muted-foreground">Initializing secure payment...</p>
    </div>

    <!-- Checkout Form Container -->
    <div v-show="!loading && !paymentComplete" class="space-y-4">
      <!-- Payment Element Container -->
      <div ref="paymentElementContainer" data-payment-element class="min-h-[200px]"></div>

      <!-- Error Display -->
      <div v-if="paymentError" class="text-sm text-red-600 bg-red-50 p-3 rounded">
        {{ paymentError }}
      </div>

      <!-- Payment Button -->
      <Button
        @click="confirmPayment"
        :disabled="processing || !paymentElementReady || !paymentFormValid"
        class="w-full h-12"
      >
        <Icon v-if="processing" name="lucide:loader-2" class="w-4 h-4 animate-spin mr-2" />
        {{ processing ? 'Processing...' : `Pay $${amount}` }}
      </Button>

      <!-- Cancel Button -->
      <Button
        @click="$emit('cancel')"
        variant="outline"
        class="w-full"
      >
        Cancel
      </Button>
    </div>

    <!-- Success State -->
    <div v-show="paymentComplete" class="text-center py-8 space-y-4">
      <div class="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
        <Icon name="heroicons:check-circle" class="w-8 h-8 text-green-600" />
      </div>
      <div>
        <h3 class="text-lg font-semibold text-green-700">Payment Successful!</h3>
        <p class="text-sm text-muted-foreground">
          Your {{ amount }} credits have been added to your account.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  clientSecret: {
    type: String,
    required: true
  },
  amount: {
    type: Number,
    required: true
  },
  currency: {
    type: String,
    default: 'aud'
  }
})

const emit = defineEmits(['success', 'error', 'cancel'])

// State
const loading = ref(true)
const processing = ref(false)
const paymentComplete = ref(false)
const paymentElementReady = ref(false)
const paymentFormValid = ref(false)
const paymentError = ref('')

// Template ref for payment element container
const paymentElementContainer = ref(null)

// Stripe instances
let stripe = null
let checkout = null
let paymentElement = null

// Initialize Stripe and Checkout
onMounted(async () => {
  try {
    console.log('🔷 Initializing embedded Stripe checkout...')
    
    // Wait for Stripe.js (basil version) to be available
    const waitForStripe = () => {
      return new Promise((resolve, reject) => {
        const maxWait = 10000 // 10 seconds
        const startTime = Date.now()
        
        const checkStripe = () => {
          if (typeof window !== 'undefined' && window.Stripe) {
            resolve(window.Stripe)
          } else if (Date.now() - startTime > maxWait) {
            reject(new Error('Stripe.js (basil) failed to load within 10 seconds'))
          } else {
            setTimeout(checkStripe, 100)
          }
        }
        checkStripe()
      })
    }

    const StripeLoader = await waitForStripe()
    const config = useRuntimeConfig()
    stripe = StripeLoader(config.public.stripe_publishable_key)
    
    if (!stripe) {
      throw new Error('Failed to initialize Stripe for embedded checkout')
    }

    console.log('🔷 Stripe (basil) loaded for embedded checkout')

    // Initialize checkout with client secret
    const fetchClientSecret = () => Promise.resolve(props.clientSecret)
    
    checkout = await stripe.initCheckout({ 
      fetchClientSecret,
      elementsOptions: {
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#635bff',
          }
        }
      }
    })

    console.log('🔷 Checkout initialized:', checkout)

    // Set loading to false first to trigger DOM render
    loading.value = false
    
    // Wait for DOM to be ready and render
    await nextTick()
    
    // Create payment element after DOM is ready
    paymentElement = checkout.createPaymentElement()
    console.log('🔷 Payment element created, preparing to mount...')
    
    // Wait for the template ref to be available
    let attempts = 0
    const maxAttempts = 50 // 5 seconds max
    while (!paymentElementContainer.value && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100))
      attempts++
    }
    
    if (!paymentElementContainer.value) {
      throw new Error('Payment element container not found after waiting')
    }
    
    console.log('🔷 Payment element container found, mounting...')
    paymentElement.mount(paymentElementContainer.value)
    
    // Listen for changes in the payment element to track validity
    paymentElement.on('change', (event) => {
      console.log('🔷 Payment element changed:', event)
      paymentFormValid.value = event.complete && !event.error
      
      // Clear any previous errors when user starts typing
      if (event.error) {
        paymentError.value = ''
      }
    })
    
    paymentElementReady.value = true
    console.log('🔷 Payment element mounted successfully')

  } catch (error) {
    console.error('🔷 Error initializing Stripe checkout:', error)
    paymentError.value = 'Failed to initialize payment form. Please try again.'
    emit('error', error)
    loading.value = false
  }
})

// Confirm payment
const confirmPayment = async () => {
  if (!checkout) return

  processing.value = true
  paymentError.value = ''

  try {
    console.log('🔷 Confirming payment...')
    
    const result = await checkout.confirm()
    
    console.log('🔷 Payment confirmation result:', result)

    if (result.type === 'error') {
      paymentError.value = result.error.message
      console.error('🔷 Payment error:', result.error)
      emit('error', result.error)
    } else {
      // Payment successful
      paymentComplete.value = true
      console.log('🔷 Payment successful!')
      
      // Emit success with payment details
      emit('success', {
        sessionId: result.session?.id,
        amount: props.amount,
        currency: props.currency
      })
    }

  } catch (error) {
    console.error('🔷 Error confirming payment:', error)
    paymentError.value = 'Payment confirmation failed. Please try again.'
    emit('error', error)
  } finally {
    processing.value = false
  }
}

// Cleanup
onUnmounted(() => {
  if (paymentElement) {
    paymentElement.unmount()
  }
})
</script>

<style scoped>
.embedded-stripe-checkout {
  min-height: 300px;
}

/* Ensure payment element container is properly sized */
#payment-element {
  /* Payment element will size itself */
}
</style> 